import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Map, Route, DollarSign, Users,
  CheckCircle2, Circle, ArrowRight, Loader2,
} from 'lucide-react';

interface OnboardingStepData {
  sort: number;
  code: string;
  name: Record<string, string>;
  description?: Record<string, string>;
  route_path: string;
  icon: string;
  completed: boolean;
}

interface OnboardingStatus {
  status: string;
  onboarding_step: number;
  steps: OnboardingStepData[];
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  MapPin, Map, Route, DollarSign, Users,
};

export default function CarrierOnboarding() {
  const { user, checkAuth } = useAuthContext();
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.carrier_id) return;
    api.get<OnboardingStatus>('/api/v1/carriers/onboarding-status')
      .then((res) => setStatus(res.data as OnboardingStatus))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.carrier_id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">{t.onboarding.failedToLoad}</p>
      </div>
    );
  }

  const completedCount = status.steps.filter((s) => s.completed).length;
  const totalCount = status.steps.length;
  const progress = Math.round((completedCount / totalCount) * 100);

  const currentStep = status.steps.find((s) => !s.completed) ?? status.steps[totalCount - 1];

  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      <div className="mx-auto max-w-3xl space-y-8">

        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">{t.onboarding.title}</h1>
          <p className="text-muted-foreground">
            {t.onboarding.subtitle.replace('{count}', String(totalCount))}
          </p>
        </div>

        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              {t.onboarding.progressLabel
                .replace('{completed}', String(completedCount))
                .replace('{total}', String(totalCount))}
            </span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {status.steps.map((step) => {
            const IconComponent = ICON_MAP[step.icon] ?? Circle;
            const isNext = step.sort === currentStep?.sort;
            const stepName = step.name[lang] ?? step.name.en;
            const stepDesc = step.description?.[lang] ?? step.description?.en;
            return (
              <Card
                key={step.code}
                className={`transition-all ${
                  step.completed
                    ? 'opacity-60'
                    : isNext
                    ? 'border-primary shadow-sm'
                    : 'border-border'
                }`}
              >
                <CardContent className="flex items-center gap-4 p-4">
                  {/* Step number / check */}
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 ${
                    step.completed
                      ? 'border-primary bg-primary text-primary-foreground'
                      : isNext
                      ? 'border-primary text-primary'
                      : 'border-muted-foreground/30 text-muted-foreground'
                  }`}>
                    {step.completed ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <IconComponent className="h-5 w-5" />
                    )}
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{stepName}</p>
                      {isNext && !step.completed && (
                        <Badge variant="outline" className="text-xs text-primary border-primary">
                          {t.onboarding.next}
                        </Badge>
                      )}
                      {step.completed && (
                        <Badge variant="secondary" className="text-xs">{t.onboarding.done}</Badge>
                      )}
                    </div>
                    {stepDesc && (
                      <p className="text-sm text-muted-foreground truncate">{stepDesc}</p>
                    )}
                  </div>

                  {/* Action button */}
                  {!step.completed && (
                    <Button
                      size="sm"
                      variant={isNext ? 'default' : 'outline'}
                      onClick={() => navigate(step.route_path)}
                    >
                      {isNext ? (
                        <>{t.onboarding.next} <ArrowRight className="ms-1 h-4 w-4" /></>
                      ) : (
                        t.onboarding.start
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* All done state */}
        {completedCount === totalCount && (
          <Card className="border-primary bg-primary/5">
            <CardHeader className="text-center pb-2">
              <CheckCircle2 className="mx-auto h-12 w-12 text-primary mb-2" />
              <CardTitle>{t.onboarding.setupComplete}</CardTitle>
              <CardDescription>{t.onboarding.setupCompleteDesc}</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center">
              <Button onClick={async () => { await checkAuth(); navigate('/dashboard'); }}>
                {t.onboarding.goToDashboard} <ArrowRight className="ms-1 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
